import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import type {Workstream, WorkstreamBasic} from 'client';
import {useAtomValue, useSetAtom} from 'jotai';
import {useMemo, useRef, useState} from 'react';
import {
  buildSubworkstreamsEdit,
  getSubworkstreamIds,
} from 'views/workstreams/detail/subworkstreamEdit';
import {
  useWorkstreamAncestors,
  useWorkstreamCreate,
  useWorkstreamEdit,
  useWorkstreamTypeaheadSearch,
} from 'views/workstreams/hooks';
import {getWorkstreamDri, isWorkstreamStatusArchived} from 'views/workstreams/utils';
import {buildDefaultWorkstreamPayload} from 'views/workstreams/WorkstreamCreate';

import {invalidateNavTrees} from './useNavTree';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

const FIELD_WIDTH_PX = 400;
const MAX_MATCHES = 6;
// The Create row's value (real workstream ids are positive).
const CREATE_VALUE = -1;

type Row = WorkstreamBasic | {create: string};

/**
 * /workstreams: the detail page's inline "add a sub-workstream".
 * Typing finds existing workstreams to link (the usual case: rolling a team's
 * workstream up under this one), with "Create …" pinned last so Enter on the
 * first row links rather than duplicates. A pick saves right away: linking
 * edits this workstream's sub-workstreams; creating makes the workstream (DRI:
 * you, privacy: this one's, so a private parent's child isn't public) and links
 * it. Nothing is saved before a pick, so Esc leaves nothing behind. Skips this
 * workstream, its parents (a loop), and ones already linked.
 */
export const AddSubworkstreamField = ({
  workstream,
  onDone,
}: {
  workstream: Workstream;
  onDone: () => void;
}) => {
  const [input, setInput] = useState('');
  const query = input.trim();
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const {createWorkstream, isPending: isCreating} = useWorkstreamCreate();
  const {editWorkstream, isPending: isLinking} = useWorkstreamEdit();
  const {ancestors} = useWorkstreamAncestors(workstream.id);
  const {data: found, isLoading} = useWorkstreamTypeaheadSearch({input});
  const containerRef = useRef<HTMLElement | null>(null);
  const isSaving = isCreating || isLinking;

  const linkedIds = useMemo(() => getSubworkstreamIds(workstream), [workstream]);
  const matches = useMemo(() => {
    const blocked = new Set([workstream.id, ...linkedIds, ...ancestors.map(({id}) => id)]);
    return (found ?? [])
      .filter(
        (candidate) =>
          !blocked.has(candidate.id) &&
          // Trackers and archived workstreams aren't sub-workstream material (as on /workstreams).
          candidate.status_cadence !== 'none' &&
          !candidate.archived &&
          !isWorkstreamStatusArchived(candidate)
      )
      .slice(0, MAX_MATCHES);
  }, [found, workstream.id, linkedIds, ancestors]);
  // No Create when a workstream already has exactly this name: link that one.
  const exactMatch = (found ?? []).some(
    (candidate) => candidate.name.trim().toLowerCase() === query.toLowerCase()
  );
  const rows: Row[] = query && !exactMatch ? [...matches, {create: query}] : matches;

  const fail = (error: unknown) =>
    setSnackbarMessage({
      text: error instanceof Error && error.message ? error.message : 'Something went wrong',
    });

  const link = async (id: number) => {
    await editWorkstream({data: buildSubworkstreamsEdit(workstream, [...linkedIds, id])});
    void invalidateNavTrees();
  };

  const handleSelection = async (value: number) => {
    if (isSaving) return;
    try {
      if (value === CREATE_VALUE) {
        if (!query || !employee.user_id) return;
        analyticsLogger().logEvent('WORKSTREAM_VIEW_CLICKED', {
          source: 'workstreams4_add_subworkstream',
          type: 'new_workstream',
          v4: true,
        });
        const id = await createWorkstream({
          data: {
            ...buildDefaultWorkstreamPayload(employee.user_id, 'workstream'),
            name: query,
            privacy: workstream.privacy,
            private_employees: workstream.private_employees?.map(({user_id}) => user_id) ?? [],
            private_teams: workstream.private_teams?.map(({team_id}) => team_id) ?? [],
          },
        });
        await link(id);
        logWorkstreamsInteraction('subworkstream_created', {
          workstream_id: workstream.id,
          subworkstream_id: id,
        });
      } else {
        await link(value);
        logWorkstreamsInteraction('subworkstream_linked', {
          workstream_id: workstream.id,
          subworkstream_id: value,
        });
      }
      onDone();
    } catch (error) {
      fail(error);
    }
  };

  const renderRow = (row: Row) =>
    'create' in row ? (
      <Typeahead.Row
        key="create"
        value={CREATE_VALUE}
        withLeftAccessory={isCreating ? <Spinner size="small" /> : <UIIcon src={AddLine} />}
        withTitle={<Text isBold>{`Create "${row.create}" as a sub-workstream`}</Text>}
      />
    ) : (
      <Typeahead.Row
        key={row.id}
        value={row.id}
        withLeftAccessory={<UIIcon src={WorkstreamsLine} />}
        withTitle={<Text style={{wordBreak: 'break-word'}}>{row.name}</Text>}
        withSubtitle={getWorkstreamDri(row)?.name}
      />
    );

  return (
    <Box style={{width: FIELD_WIDTH_PX, maxWidth: '100%', marginBottom: 12}}>
      <Typeahead.Wrapper onSelection={(value: number) => void handleSelection(value)}>
        {({getTriggerProps, getContentProps}) => (
          <>
            <TextInput.Container ref={containerRef} size="small">
              <TextInput.Input
                autoFocus
                autoComplete="off"
                disabled={isSaving}
                placeholder="Find a workstream, or name a new one"
                aria-label="Add a sub-workstream"
                value={input}
                {...getTriggerProps({
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setInput(event.currentTarget.value),
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      onDone();
                    }
                  },
                  // An empty field just closes on leaving; a typed one waits for a pick or Esc.
                  onBlur: () => {
                    if (!query) onDone();
                  },
                })}
              />
            </TextInput.Container>
            <Typeahead.Container {...getContentProps()} triggerRef={containerRef}>
              {query && isLoading && rows.length <= 1 ? (
                <Box display="flex" justifyContent="center" padding="16">
                  <Spinner size="small" />
                </Box>
              ) : rows.length > 0 ? (
                <Typeahead.Results
                  results={rows}
                  renderRow={renderRow}
                  initialResults={rows.length}
                  maxResults={rows.length}
                />
              ) : null}
            </Typeahead.Container>
          </>
        )}
      </Typeahead.Wrapper>
    </Box>
  );
};
